import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { map, shareReplay, switchMap, take } from 'rxjs';

import { Logger } from '@mobi/rwc-utils-common-ng-jslib';

import { UserInfoService } from '../../auth/user-info/user-info.service';
import { UserInfo } from '../../auth/user-info/user-info.types';
import { IMPERSONATE_ACTION, ImpersonationUrlListenerService } from '../impersonation-url-listener.service';
import { ON_BEHALF_OF_QUERY_PARAM, RESET_ON_BEHALF_OF_QUERY_PARAM } from '../impersonation.consts';

import { BpsImpersonationIntegrationService } from './bps-impersonation.integration.service';

import { ImpersonationService } from '../impersonation.service';

const ON_BEHALF_OF_STORAGE_KEY = 'mobi.on_behalf_of';

export class BpsImpersonationService extends ImpersonationService {
    #logger = inject(Logger);
    #router = inject(Router);
    #impersonationIntegrationService = inject(BpsImpersonationIntegrationService);
    #window: Window = inject(DOCUMENT).defaultView as Window;
    #storage = this.#window.localStorage;
    #url$ = inject(ImpersonationUrlListenerService).url$;

    get onBehalfOf() {
        return this.#storage.getItem(ON_BEHALF_OF_STORAGE_KEY) || undefined;
    }

    constructor() {
        super();
        this.#initUrlListeners();
    }

    readonly impersonableUsers$ = inject(UserInfoService).loggedInUser.pipe(
        map((userinfo: UserInfo) => userinfo.email ?? userinfo.userId),
        switchMap((email: string | undefined) => this.#impersonationIntegrationService.getImpersonationMapping(email)),
        shareReplay(1)
    );
    readonly allowedTestUsers$ = this.impersonableUsers$.pipe(
        map(users => users.map(u => ({ userId: u.userId, anzeigeName: u.label, name: '', vorName: '' })))
    );

    #initUrlListeners(): void {
        this.#url$.subscribe(async ([action, url]) => {
            if (action === RESET_ON_BEHALF_OF_QUERY_PARAM || action === ON_BEHALF_OF_QUERY_PARAM) {
                this.#handleNextOnBehalfOfValue(url, null);
                await this.#resetUser(url);
            } else if (action === IMPERSONATE_ACTION) {
                this.#handleNextOnBehalfOfValue(url, url.searchParams.get(ON_BEHALF_OF_QUERY_PARAM));
            }
        });
    }

    /**
     * if the given name is falsy, impersonation will be reset
     */
    switchUser(userName?: string): Promise<boolean> {
        const queryParams = new URLSearchParams(this.#window.location.search);
        if (userName) {
            queryParams.set(ON_BEHALF_OF_QUERY_PARAM, userName);
            queryParams.delete(RESET_ON_BEHALF_OF_QUERY_PARAM);
        } else {
            queryParams.set(RESET_ON_BEHALF_OF_QUERY_PARAM, 'true');
            queryParams.delete(ON_BEHALF_OF_QUERY_PARAM);
        }
        return this.#router.navigate([this.#window.location.pathname], {
            queryParams: Object.fromEntries(queryParams)
        });
    }

    #resetUser(url: URL): Promise<boolean> {
        const queryParams = new URLSearchParams(url.searchParams);
        queryParams.delete(ON_BEHALF_OF_QUERY_PARAM);
        queryParams.delete(RESET_ON_BEHALF_OF_QUERY_PARAM);
        return this.#router.navigate([url.pathname], { queryParams: Object.fromEntries(queryParams) });
    }

    #handleNextOnBehalfOfValue(url: URL, nextOnBehalfOf: string | null): void {
        const currentOnBehalfOf = this.onBehalfOf ?? null;
        if (nextOnBehalfOf !== currentOnBehalfOf) {
            this.#logger.info(`ON_BEHALF_OF changed (current: ${currentOnBehalfOf}, new: ${nextOnBehalfOf})`);
            this.impersonableUsers$
                .pipe(
                    take(1),
                    map(users => users.some(testUser => testUser.userId === nextOnBehalfOf))
                )
                .subscribe(hasPermissionForTestUser => {
                    if (!nextOnBehalfOf || hasPermissionForTestUser) {
                        if (nextOnBehalfOf) {
                            this.#storage.setItem(ON_BEHALF_OF_STORAGE_KEY, nextOnBehalfOf);
                        } else {
                            this.#storage.removeItem(ON_BEHALF_OF_STORAGE_KEY);
                        }
                        this.#logger.info('reloading app...');
                        this.#window.location.href = url.href;
                    } else {
                        this.#logger.error(
                            new Error(
                                `User does not have permission to impersonate user ${nextOnBehalfOf}. Make sure you have the necessary permission!`
                            )
                        );
                    }
                });
        }
    }
}
